import { createSlice } from '@reduxjs/toolkit'
import {
  getLandingUrlDto,
  getLandingUrlState,
} from 'features/creative/creativeUtil'
import { validateLandingUrlWithSiteUrl } from 'features/creative/add/common/useCreativeValidation'
import { BizChannelTypes } from '../../../Codes'

const initialState = {
  title: '',
  description: '',
  landingUrl: {
    individual: false,
    mobileUrl: '',
    pcUrl: '',
  },
  licenseReview: false,
  licenseNumber: '',
  licenseDocument: null,
  license: {
    customLicenseNumber: '',
    customLicenseDocument: null,
    recommendLicenseNumber: '',
    recommendLicenseDocument: null,
  },
  bothLegalNoticeChecked: false,
}

export const validateBasicCreative = (bizChannelType, state, siteUrl) => ({
  title:
    bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL &&
    state.title === '' &&
    '제목을 입력하세요.',
  description:
    bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL &&
    state.description === '' &&
    '설명문구를 입력하세요.',
  ...(bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL &&
    validateLandingUrlWithSiteUrl(state.landingUrl, siteUrl)),
  licenseNumber:
    bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL &&
    state.licenseReview &&
    state.licenseNumber === '' &&
    '의료심의필번호를 입력하세요.',
  licenseDocument:
    bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL &&
    state.licenseReview &&
    ((state.licenseDocument === null && '심사서류를 선택하세요.') ||
      (state.licenseDocument?.deleted &&
        '삭제된 심사서류입니다. 다른 서류를 선택하세요.')),
  customLicenseNumber:
    bizChannelType === BizChannelTypes.TALK_CHANNEL &&
    state.licenseReview &&
    state.bothLegalNoticeChecked &&
    state.license.customLicenseNumber.trim() === '' &&
    '법적고지문구를 입력하세요.',
  customLicenseDocument:
    bizChannelType === BizChannelTypes.TALK_CHANNEL &&
    state.licenseReview &&
    state.bothLegalNoticeChecked &&
    state.license.customLicenseDocument === null &&
    '심사서류를 선택하세요.',
  recommendLicenseNumber:
    bizChannelType === BizChannelTypes.TALK_CHANNEL &&
    state.licenseReview &&
    state.bothLegalNoticeChecked &&
    state.license.recommendLicenseNumber.trim() === '' &&
    '법적고지문구를 입력하세요.',
  recommendLicenseDocument:
    bizChannelType === BizChannelTypes.TALK_CHANNEL &&
    state.licenseReview &&
    state.bothLegalNoticeChecked &&
    state.license.recommendLicenseDocument === null &&
    '심사서류를 선택하세요.',
})

export const getBasicCreativeData = (state, bizChannelType) => {
  const baseBasicCreative = !state.bothLegalNoticeChecked
    ? {
        ...(state.licenseNumber !== '' && {
          licenseNumber: state.licenseNumber,
        }),
        ...(state.licenseDocument && {
          reviewDocumentId: state.licenseDocument.id,
        }),
      }
    : {
        license: {
          customLicenseNumber: state.license.customLicenseNumber,
          customLicenseDocumentId: state.license.customLicenseDocument.id,
          recommendLicenseNumber: state.license.recommendLicenseNumber,
          recommendLicenseDocumentId: state.license.recommendLicenseDocument.id,
        },
      }

  return bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL
    ? {
        ...baseBasicCreative,
        licenseReviewYn: state.licenseReview ? 'Y' : 'N',
        title: state.title,
        description: state.description,
        landingUrl: getLandingUrlDto(state.landingUrl),
      }
    : {
        ...baseBasicCreative,
        licenseReviewYn:
          state.licenseNumber !== '' || state.bothLegalNoticeChecked
            ? 'Y'
            : 'N',
      }
}

const basicCreativeSlice = createSlice({
  name: 'basicCreative',
  initialState,
  reducers: {
    reset: () => initialState,
    setTitle: (state, { payload: title }) => {
      state.title = title
    },
    setDescription: (state, { payload: description }) => {
      state.description = description
    },
    setIndividual: (state, { payload: checked }) => {
      state.landingUrl.individual = checked
    },
    setMobileUrl: (state, { payload: url }) => {
      state.landingUrl.mobileUrl = url
    },
    setPcUrl: (state, { payload: url }) => {
      state.landingUrl.pcUrl = url
    },
    setLicenseNumber: (state, { payload: licenseNumber }) => {
      state.licenseNumber = licenseNumber
    },
    setLicenseDocument: (state, { payload: licenseDocument }) => {
      state.licenseDocument = licenseDocument
    },
    setCustomLicenseNumber: (state, { payload: licenseNumber }) => {
      state.license.customLicenseNumber = licenseNumber
    },
    setCustomLicenseDocument: (state, { payload: licenseDocument }) => {
      state.license.customLicenseDocument = licenseDocument
    },
    setRecommendLicenseNumber: (state, { payload: licenseNumber }) => {
      state.license.recommendLicenseNumber = licenseNumber
    },
    setRecommendLicenseDocument: (state, { payload: licenseDocument }) => {
      state.license.recommendLicenseDocument = licenseDocument
    },
    setLicenseReview: (state, { payload }) => {
      state.licenseReview = payload
    },
    setBasicCreativeForm: (
      state,
      {
        payload: {
          title,
          description,
          landingUrl,
          licenseNumber,
          licenseReview,
          licenseDocument,
        },
      }
    ) => {
      state.title = title || ''
      state.description = description || ''
      if (landingUrl) {
        state.landingUrl = getLandingUrlState(landingUrl)
      }
      state.licenseNumber = licenseNumber || ''
      state.licenseDocument = licenseDocument
      state.licenseReview = licenseReview
    },
    setBasicCreativeBothLegalNoticeForm: (
      state,
      { payload: { licenseReview, license } }
    ) => {
      state.licenseReview = licenseReview
      state.license = license
    },
    setBothLegalNoticeChecked: (state, { payload }) => {
      state.bothLegalNoticeChecked = payload
    },
  },
})

export const {
  reset,
  setPcUrl,
  setMobileUrl,
  setIndividual,
  setDescription,
  setTitle,
  setLicenseDocument,
  setLicenseNumber,
  setBasicCreativeForm,
  setLicenseReview,
  setBasicCreativeBothLegalNoticeForm,
  setCustomLicenseNumber,
  setCustomLicenseDocument,
  setRecommendLicenseNumber,
  setRecommendLicenseDocument,
  setBothLegalNoticeChecked,
} = basicCreativeSlice.actions

export default basicCreativeSlice.reducer
